import {action, runInAction} from 'mobx';
import {PasswordErrorType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {ITranslate} from '../../services/LocalizationService';
import {ITsLocaleHelperService} from '../../services/ts-locale-helper/ts-locale-helper.service';
import {ITsReactStateHelper} from '../../services/ts-react-state-helper/ts-react-state-helper.service';
import {RouteName} from '../../router/route-names';
import {ChangePasswordForm} from './change-password-form.model';
import {IUserService} from '@techsee/techsee-client-infra/lib/services/UserService';
import {ChangePasswordState} from './change-password.state';

export interface IChangePasswordController {
    readonly waiting: boolean;
    readonly message: string;
    readonly dir: string;
    readonly form: ChangePasswordForm;
    readonly translate: ITranslate;

    onSubmit(): Promise<void>;
}

export class ChangePasswordController implements IChangePasswordController {
    readonly dir: string;

    readonly form: ChangePasswordForm;

    private readonly _translate: ITranslate;

    private readonly _tsLocaleHelper: ITsLocaleHelperService;

    private readonly _userId: string;

    private readonly _tsStateHelper: ITsReactStateHelper;

    private readonly _changePasswordState: ChangePasswordState;

    constructor(
        translate: ITranslate,
        tsLocaleHelper: ITsLocaleHelperService,
        userId: string,
        tsStateHelper: ITsReactStateHelper,
        form: ChangePasswordForm,
        private userService: IUserService,
        changePasswordState: ChangePasswordState
    ) {
        this._translate = translate;
        this._tsLocaleHelper = tsLocaleHelper;
        this._userId = userId;
        this._tsStateHelper = tsStateHelper;
        this.form = form;
        this.dir = this._tsLocaleHelper.isRTL() ? 'rtl' : 'ltr';
        this._changePasswordState = changePasswordState;
    }

    get translate(): ITranslate {
        return this._translate;
    }

    get waiting(): boolean {
        return this._changePasswordState.waiting;
    }

    get message(): string {
        return this._changePasswordState.message;
    }

    @action
    async onSubmit(): Promise<void> {
        this._changePasswordState.message = '';

        const isValid = await this.form.validateForm();

        if (!isValid) {
            runInAction(() => {
                this._changePasswordState.waiting = false;
            });
            this.form.displayFieldsErrors();

            return;
        }

        runInAction(() => {
            this._changePasswordState.waiting = true;
        });

        const formValues = this.form.getFormValues();
        const {oldPassword, newPassword} = formValues;

        if (!oldPassword || !newPassword) {
            runInAction(() => {
                this._changePasswordState.waiting = false;
                this._changePasswordState.message = this._translate('CHANGE_PASSWORD.CONTROLLER.ERROR');
            });

            return;
        }

        try {
            const userServiceAny = this.userService as any;

            // Check available methods for debugging
            if (typeof userServiceAny.updatePassword !== 'function') {
                console.error(
                    'updatePassword method not found. Available methods:',
                    Object.getOwnPropertyNames(userServiceAny).filter(
                        (name) => typeof userServiceAny[name] === 'function'
                    )
                );
                throw new Error('updatePassword method does not exist on userService');
            }

            await userServiceAny.updatePassword(this._userId, {
                oldPassword: oldPassword,
                newPassword: newPassword
            });

            await this._tsStateHelper.navigateTo(RouteName.INVITE);
        } catch (err: any) {
            runInAction(() => {
                this._changePasswordState.waiting = false;

                if (err.data && err.data.message === PasswordErrorType.usedPassword) {
                    this._changePasswordState.message = this._translate('CHANGE_PASSWORD.CONTROLLER.USED_PASSWORD');

                    return;
                }

                if (err.data && err.data.message === PasswordErrorType.passwordMismatch) {
                    this._changePasswordState.message = this._translate(
                        'CHANGE_PASSWORD.CONTROLLER.OLD_PASSWORD_INVALID'
                    );

                    return;
                }

                console.error('Change password error:', err);
                this._changePasswordState.message = this._translate('CHANGE_PASSWORD.CONTROLLER.ERROR');
            });
        }
    }
}
